/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import axios, { AxiosPromise, Method } from 'axios';

import { createApiRef, DiscoveryApi } from '@backstage/core-plugin-api';

import {
  ClientApiConfig,
  AddCommentRequest,
  AddCommentResponse,
  CreateTicketRequest,
  CreateTicketResponse,
  JiraApptioApi,
} from './types';

export const jiraApptioApiRef = createApiRef<JiraApptioApi>({
  id: 'plugin.jira.apptio.api',
});

const JIRA_APPTIO_BASE_URL = 'https://apptio.atlassian.net';

function getLinkFromKey(ticketKey: string) {
  // The default self link points at the API Proxy, not the site
  return `${JIRA_APPTIO_BASE_URL}/browse/${ticketKey}`;
}

export class JiraApptioClient implements JiraApptioApi {
  static fromConfig(discovery: DiscoveryApi) {
    return new JiraApptioClient({ discovery });
  }

  private readonly config: ClientApiConfig;

  constructor(config: ClientApiConfig) {
    this.config = config;
  }

  private getJiraApiUrl(): Promise<string> {
    return new Promise<string>(async (resolve, reject) => {
      try {
        const proxyUrl = await this.config.discovery.getBaseUrl('proxy');
        const jiraApiUrl = `${proxyUrl}/jira/api`;

        resolve(jiraApiUrl);
      } catch (err) {
        reject(err);
      }
    });
  }

  private jiraRequest(
    uri: string,
    method: Method,
    data: any,
  ): Promise<AxiosPromise> {
    return new Promise<AxiosPromise>(async (resolve, reject) => {
      try {
        const baseUrl = await this.getJiraApiUrl();
        const url = `${baseUrl}/rest/api/2/${uri}`;

        const response = axios({
          url,
          method,
          data,
          timeout: 20_000,
        });

        resolve(response);
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * Creates a Jira ticket
   *
   */
  async createTicket(
    request: CreateTicketRequest,
  ): Promise<CreateTicketResponse> {
    const {
      summary,
      description,
      project,
      issueType,
      priority,
      requestingTeam,
    } = request;

    if (project === 'PRE' && !requestingTeam) {
      throw new Error('requestingTeam is required for PRE tickets');
    }

    const ticket = {
      fields: {
        project: {
          key: project,
        },
        issuetype: {
          name: issueType,
        },
        summary,
        description,
        priority: {
          id: priority,
        },
        customfield_16031: {
          // Requesting Team
          value: requestingTeam,
        },
      },
    };

    const response = await this.jiraRequest('issue', 'POST', ticket);

    const createTicketResponse: CreateTicketResponse = {
      ticketId: response.data?.id,
      ticketKey: response.data?.key,
      ticketLink: getLinkFromKey(response.data?.key),
    };

    return createTicketResponse;
  }

  /**
   * Adds a comment to a Jira ticket
   *
   */
  addComment(request: AddCommentRequest): Promise<AddCommentResponse> {
    const { ticketId, comment } = request;
    const commentData = {
      body: comment,
    };

    return new Promise<AddCommentResponse>(async (resolve, reject) => {
      try {
        const response = await this.jiraRequest(
          `issue/${ticketId}/comment`,
          'POST',
          commentData,
        );
        const AddCommentResponse: AddCommentResponse = {
          commentLink: response.data?.self,
        };
        resolve(AddCommentResponse);
      } catch (err) {
        reject(err);
      }
    });
  }
}
